/**
 * Rule-based scoring utilities shared across SEO tools. Pure functions; no
 * side effects, no API calls, no AI usage. Keep deterministic & easy to audit.
 */

import {
    CATEGORY_BASE_RANGES,
    adjustedBand,
    sumBands,
    HVAC_USEFUL_LIFE,
    ROOF_USEFUL_LIFE,
    ROOF_RANGES,
    HVAC_RANGES,
    ELECTRICAL_RANGES,
    FOUNDATION_RANGES,
    WATER_INTRUSION_RANGES,
} from "./ranges";

/* =========================================================================
 *  Generic severity → concern level mapping
 * ========================================================================= */

export const CONCERN_LEVELS = ["low", "moderate", "high"];

export function bandFromCategory(category, opts) {
    const base = CATEGORY_BASE_RANGES[category];
    if (!base) return null;
    return adjustedBand(base, opts);
}

function concernFromScore(score) {
    if (score >= 14) return "high";
    if (score >= 7) return "moderate";
    return "low";
}

/* =========================================================================
 *  Tool 1 — Home Inspection Negotiation Calculator
 * ========================================================================= */

const NEGOTIATION_CATEGORY_WEIGHTS = {
    foundation: 6,
    structure: 6,
    roof: 5,
    water_intrusion: 5,
    electrical: 4,
    hvac: 3,
    plumbing: 3,
    mold_pest: 4,
    drainage: 3,
    safety: 4,
    other: 1,
};

export function scoreNegotiation({
    homePrice = 0,
    zipCode = "",
    issues = [],
    repairExposureLow = 0,
    repairExposureHigh = 0,
    sellerOffered = "no_offer",
    buyerGoal = "understand_options",
    underContract = "not_yet",
    deadlineDays = null,
} = {}) {
    let score = 0;
    const reasons = [];
    for (const i of issues) {
        const w = NEGOTIATION_CATEGORY_WEIGHTS[i] ?? 1;
        score += w;
        if (w >= 5) reasons.push(i);
    }
    if (issues.length >= 3) score += 2;
    if (repairExposureHigh && homePrice && repairExposureHigh / homePrice > 0.03) {
        score += 3;
    }
    if (sellerOffered === "no_offer") score += 1;
    if (underContract === "yes" && deadlineDays != null && deadlineDays <= 3) {
        score += 1;
    }

    const level = concernFromScore(score);

    // Suggested discussion range — only when buyer supplied repair exposure
    let suggestedRange = null;
    if (repairExposureLow > 0 && repairExposureHigh >= repairExposureLow) {
        const lo = Math.round((repairExposureLow * 0.5) / 50) * 50;
        const hi = Math.round((repairExposureHigh * 0.85) / 50) * 50;
        suggestedRange = { lo, hi };
    }

    const focusCategories = Array.from(new Set(reasons)).slice(0, 4);

    const specialistFollowUp = [];
    if (issues.includes("foundation") || issues.includes("structure"))
        specialistFollowUp.push("Structural engineer");
    if (issues.includes("roof")) specialistFollowUp.push("Roofing contractor");
    if (issues.includes("electrical"))
        specialistFollowUp.push("Licensed electrician");
    if (issues.includes("hvac")) specialistFollowUp.push("HVAC technician");
    if (issues.includes("plumbing")) specialistFollowUp.push("Licensed plumber");
    if (issues.includes("water_intrusion") || issues.includes("drainage"))
        specialistFollowUp.push("Waterproofing / drainage specialist");
    if (issues.includes("mold_pest"))
        specialistFollowUp.push("Mold or pest specialist");

    const nextSteps = [
        "Review your inspection report's flagged items in detail with your agent.",
        "Confirm which items are safety, structural, or major-system concerns.",
    ];
    if (specialistFollowUp.length) {
        nextSteps.push(
            "Request follow-up quotes from licensed professionals where relevant.",
        );
    }
    if (buyerGoal === "credit") {
        nextSteps.push(
            "Prepare a written request prioritizing high-impact items for a seller credit.",
        );
    } else if (buyerGoal === "seller_repair") {
        nextSteps.push(
            "Specify scope clearly — who, by when, with what documentation — for any seller-completed repairs.",
        );
    } else if (buyerGoal === "price_reduction") {
        nextSteps.push(
            "Frame the conversation around documented repair exposure rather than a flat reduction.",
        );
    }
    if (deadlineDays != null && deadlineDays <= 5) {
        nextSteps.push(
            "Move quickly — keep follow-up quotes and your written ask inside the contingency window.",
        );
    }

    return {
        level,
        score,
        focusCategories,
        suggestedRange,
        specialistFollowUp,
        nextSteps,
        confidence:
            issues.length === 0 ? "low" : issues.length >= 4 ? "medium" : "medium",
    };
}

/* =========================================================================
 *  Tool 2 — Repair Exposure Calculator
 * ========================================================================= */

export function calcRepairExposure({
    homeAge = null,
    propertyType = "single_family",
    issues = [],
    severity = "unknown",
    urgency = "unknown",
} = {}) {
    const opts = { severity, urgency, homeAge };
    const bands = issues
        .map((i) => bandFromCategory(i, opts))
        .filter(Boolean);
    const total = sumBands(bands);

    let confidence = "medium";
    if (severity === "unknown") confidence = "low";
    if (issues.length >= 3 && severity !== "unknown") confidence = "high";

    const drivers = [];
    if (issues.includes("foundation") || issues.includes("structure"))
        drivers.push("Structural concerns drive the upper bound significantly.");
    if (issues.includes("roof")) drivers.push("Roof age & scope affect total replacement vs. repair framing.");
    if (issues.includes("hvac"))
        drivers.push("HVAC end-of-life is often replacement, not repair.");
    if (issues.includes("water_intrusion") || issues.includes("basement_water"))
        drivers.push(
            "Water intrusion: source identification often costs less than long-term remediation.",
        );
    if (propertyType === "condo") drivers.push("HOA may cover some exterior items (roof, drainage) — verify with bylaws.");
    if (homeAge && Number(homeAge) >= 50)
        drivers.push("Older homes (50+ yrs) often surface secondary issues during repairs.");

    return {
        total,
        bands: issues.map((i) => ({ category: i, band: bandFromCategory(i, opts) })),
        confidence,
        drivers: drivers.slice(0, 5),
    };
}

/* =========================================================================
 *  Tool 3 — Should I Buy This House? Inspection Risk Quiz
 * ========================================================================= */

const RISK_WEIGHTS = {
    foundation_cracks: 5,
    roof_problems: 4,
    water_intrusion: 4,
    electrical_hazards: 5,
    plumbing_leaks: 3,
    hvac_eol: 3,
    mold_moisture: 4,
    pest_damage: 3,
    safety_hazards: 5,
};

export function scoreBuyDecision({
    homeAge = null,
    issues = [],
    deadlineSoon = false,
    riskTolerance = "medium",
    budget = "unknown",
} = {}) {
    let score = 0;
    for (const i of issues) score += RISK_WEIGHTS[i] ?? 1;
    if (Number(homeAge) >= 50) score += 2;
    if (deadlineSoon) score += 1;
    if (budget === "low") score += 2;
    if (riskTolerance === "low" && score > 4) score += 2;

    const level = concernFromScore(score);

    const specialists = [];
    if (issues.includes("foundation_cracks")) specialists.push("Structural engineer");
    if (issues.includes("roof_problems")) specialists.push("Roofing contractor");
    if (issues.includes("electrical_hazards")) specialists.push("Licensed electrician");
    if (issues.includes("plumbing_leaks")) specialists.push("Licensed plumber");
    if (issues.includes("hvac_eol")) specialists.push("HVAC technician");
    if (issues.includes("water_intrusion")) specialists.push("Waterproofing / drainage specialist");
    if (issues.includes("mold_moisture")) specialists.push("Mold remediation specialist");
    if (issues.includes("pest_damage")) specialists.push("Pest / wood-damage specialist");

    const clarifications = [
        "Has the seller disclosed any prior repairs in these categories?",
        "Are there visible signs of recurring or worsening damage?",
        "What is the realistic budget for first-year repairs?",
    ];
    if (deadlineSoon) {
        clarifications.push(
            "What is the soonest you can get qualified follow-up assessments?",
        );
    }

    return {
        level,
        score,
        specialists,
        clarifications,
    };
}

/* =========================================================================
 *  Tool 4 — Seller Credit Estimator
 * ========================================================================= */

const CONCESSION_LIMITS = {
    conventional: 0.06,
    fha: 0.06,
    va: 0.04,
    cash: 0.1,
    unsure: 0.06,
};

export function estimateSellerCredit({
    homePrice = 0,
    repairLow = 0,
    repairHigh = 0,
    issues = [],
    buyerPref = "unsure",
    marketCondition = "balanced",
    financingType = "unsure",
} = {}) {
    if (!repairLow && !repairHigh) {
        return {
            level: "low",
            range: null,
            warning:
                "Provide an estimated repair exposure to see a discussion range.",
            tips: [
                "Use the Repair Exposure Calculator first to estimate scope.",
                "Document key issues before opening the conversation.",
            ],
        };
    }

    // Base: 50–80% of repair exposure as a discussion starting point.
    let lo = repairLow * 0.5;
    let hi = repairHigh * 0.8;

    // Market modifiers
    if (marketCondition === "buyers") {
        lo *= 1.1;
        hi *= 1.2;
    } else if (marketCondition === "sellers") {
        lo *= 0.8;
        hi *= 0.9;
    }

    // Preference framing
    if (buyerPref === "credit") hi *= 1.05;

    // Lender concession cap
    const cap = (CONCESSION_LIMITS[financingType] || 0.06) * (homePrice || 0);
    const capped = homePrice > 0 ? Math.min(hi, cap) : hi;

    const level =
        issues.length >= 3 || repairHigh > 0.04 * (homePrice || 1)
            ? "high"
            : issues.length >= 1
                ? "moderate"
                : "low";

    const tips = [
        "Anchor the ask in documented repair scope rather than a round number.",
        "Get at least one contractor quote on the highest-impact issue if time allows.",
    ];
    if (financingType !== "cash" && homePrice > 0) {
        tips.push(
            `Seller credits typically cap at ${Math.round(
                (CONCESSION_LIMITS[financingType] || 0.06) * 100,
            )}% of price for ${financingType === "unsure" ? "most loan types" : financingType.toUpperCase()}.`,
        );
    }
    if (marketCondition === "sellers") {
        tips.push(
            "In a seller's market, leading with credit (vs. repairs) is often the lowest-friction ask.",
        );
    }

    return {
        level,
        range: {
            lo: Math.round(lo / 50) * 50,
            hi: Math.round(capped / 50) * 50,
        },
        cappedBy:
            homePrice > 0 && hi > cap ? "lender_concession_cap" : null,
        tips,
    };
}

/* =========================================================================
 *  Tool 5 — Foundation Crack Severity Checker
 * ========================================================================= */

export function scoreFoundationCrack({
    crackType = "unknown",
    crackWidth = "unknown",
    waterPresent = false,
    bowingShearing = false,
    location = "unknown",
    recurringMoisture = false,
    homeAge = null,
} = {}) {
    let score = 0;
    if (crackType === "horizontal") score += 6;
    if (crackType === "stair_step") score += 4;
    if (crackType === "diagonal") score += 3;
    if (crackType === "vertical") score += 1;

    if (crackWidth === "gt_quarter") score += 5;
    if (crackWidth === "eighth_to_quarter") score += 3;
    if (crackWidth === "lt_eighth") score += 1;

    if (waterPresent) score += 3;
    if (bowingShearing) score += 5;
    if (recurringMoisture) score += 2;
    if (location === "basement") score += 1;
    if (Number(homeAge) >= 60) score += 1;

    const level = concernFromScore(score);

    const reasons = [];
    if (crackType === "horizontal")
        reasons.push("Horizontal cracks can indicate lateral pressure on the wall.");
    if (bowingShearing)
        reasons.push("Bowing or shearing patterns warrant urgent structural review.");
    if (waterPresent && crackType !== "unknown")
        reasons.push("Water at the crack adds drainage / waterproofing concerns.");
    if (level === "low")
        reasons.push("Narrow vertical cracks with no movement are common in many homes.");

    const baseRange =
        level === "high"
            ? FOUNDATION_RANGES.major_repair
            : level === "moderate"
                ? FOUNDATION_RANGES.crack_repair
                : FOUNDATION_RANGES.monitor_seal;
    const band = { lo: baseRange.min, hi: baseRange.max };

    const followUps = [];
    if (level === "high") {
        followUps.push("Schedule a structural engineer for a written evaluation.");
        followUps.push("Pause major negotiation moves until the engineer's report is in hand.");
    } else if (level === "moderate") {
        followUps.push("Request an evaluation from a licensed foundation contractor.");
    } else {
        followUps.push("Monitor crack width over time; consider sealing if moisture is present.");
    }

    return { level, score, reasons, band, followUps };
}

/* =========================================================================
 *  Tool 6 — Water Intrusion Risk Checker
 * ========================================================================= */

export function scoreWaterIntrusion({
    signs = [],
    sumpPumpPresent = false,
    gradingTowardHome = false,
    gutterIssues = false,
    foundationCracks = false,
    location = "unknown",
    recurring = "unknown",
} = {}) {
    let score = 0;
    if (signs.includes("active_seepage")) score += 6;
    if (signs.includes("moisture_stains")) score += 3;
    if (signs.includes("musty_odor")) score += 3;
    if (signs.includes("efflorescence")) score += 2;
    if (gradingTowardHome) score += 2;
    if (gutterIssues) score += 2;
    if (foundationCracks) score += 3;
    if (sumpPumpPresent && signs.includes("active_seepage")) score += 2;
    if (recurring === "yes") score += 3;

    const level = concernFromScore(score);

    const sources = [];
    if (gradingTowardHome || gutterIssues)
        sources.push("Exterior drainage (grading, gutters, downspouts)");
    if (signs.includes("active_seepage") || foundationCracks)
        sources.push("Foundation / wall waterproofing");
    if (location === "basement" || location === "crawlspace")
        sources.push("Below-grade moisture management");

    const specialists = [];
    if (level === "high" || signs.includes("active_seepage"))
        specialists.push("Waterproofing contractor");
    if (gradingTowardHome || gutterIssues)
        specialists.push("Drainage contractor / landscaper");
    if (signs.includes("musty_odor")) specialists.push("Mold remediation specialist");
    if (signs.includes("moisture_stains")) specialists.push("Roofer (if upper-level stains)");

    const baseRange =
        level === "high"
            ? WATER_INTRUSION_RANGES.waterproofing
            : level === "moderate"
                ? WATER_INTRUSION_RANGES.drainage_fix
                : WATER_INTRUSION_RANGES.surface_cleanup;
    const band = { lo: baseRange.min, hi: baseRange.max };

    return { level, score, sources, specialists, band };
}

/* =========================================================================
 *  Tool 7 — Roof Repair Cost Calculator
 * ========================================================================= */

export function estimateRoof({
    roofAge = null,
    roofType = "unknown",
    issues = [],
    severity = "unknown",
} = {}) {
    const usefulLife = ROOF_USEFUL_LIFE[roofType] || ROOF_USEFUL_LIFE.unknown;
    const age = Number(roofAge) || 0;
    const eol = age >= usefulLife;

    let stance = "repair";
    if (
        eol ||
        issues.includes("end_of_life") ||
        issues.includes("soft_decking") ||
        (issues.length >= 3 && severity === "major")
    ) {
        stance = "full_replacement";
    } else if (
        issues.includes("active_leak") ||
        (issues.includes("missing_shingles") && issues.includes("damaged_flashing"))
    ) {
        stance = "partial_replacement";
    }

    const baseRange =
        stance === "full_replacement"
            ? ROOF_RANGES.full_replacement
            : stance === "partial_replacement"
                ? ROOF_RANGES.partial_replacement
                : ROOF_RANGES.repair;
    const band = { lo: baseRange.min, hi: baseRange.max };

    const level =
        stance === "full_replacement"
            ? "high"
            : stance === "partial_replacement"
                ? "moderate"
                : "low";

    const followUps = [
        "Get a written roof inspection from a licensed roofer.",
        "Ask about the underlying decking and flashing, not just the surface.",
    ];
    if (issues.includes("active_leak")) {
        followUps.unshift("Treat active leaks as time-sensitive — interior damage compounds quickly.");
    }
    if (eol) {
        followUps.push("Factor remaining useful life into any negotiation framing.");
    }

    return { level, stance, band, followUps, eol };
}

/* =========================================================================
 *  Tool 8 — HVAC Age Replacement Checker
 * ========================================================================= */

export function estimateHvac({
    systemAge = null,
    systemType = "unknown",
    workingAtInspection = "unknown",
    issues = [],
} = {}) {
    const usefulLife = HVAC_USEFUL_LIFE[systemType] || HVAC_USEFUL_LIFE.unknown;
    const age = Number(systemAge) || 0;

    let stance = "service";
    if (
        workingAtInspection === "no" ||
        issues.includes("unsafe_venting") ||
        age >= usefulLife
    ) {
        stance = "replacement";
    } else if (
        issues.includes("rust_corrosion") ||
        issues.includes("not_cooling_heating") ||
        age >= usefulLife - 4
    ) {
        stance = "major_repair";
    }

    const baseRange =
        stance === "replacement"
            ? HVAC_RANGES.replacement
            : stance === "major_repair"
                ? HVAC_RANGES.major_repair
                : HVAC_RANGES.service;
    const band = { lo: baseRange.min, hi: baseRange.max };

    const level =
        stance === "replacement"
            ? "high"
            : stance === "major_repair"
                ? "moderate"
                : "low";

    const followUps = [];
    if (issues.includes("unsafe_venting")) {
        followUps.push("Treat venting concerns as a safety follow-up before close.");
    }
    if (issues.includes("missing_service_records")) {
        followUps.push("Request maintenance / service history from the seller in writing.");
    }
    followUps.push(
        "Ask an HVAC technician to confirm replacement vs. repair in writing.",
    );

    return {
        level,
        stance,
        band,
        followUps,
        usefulLife,
        ageRelative: age >= usefulLife ? "over" : age >= usefulLife - 4 ? "near" : "within",
    };
}

/* =========================================================================
 *  Tool 9 — Electrical Panel Concern Checker
 * ========================================================================= */

const PANEL_BRAND_FLAGS = {
    federal_pacific: { weight: 6, label: "Federal Pacific Stab-Lok" },
    zinsco: { weight: 6, label: "Zinsco / Sylvania" },
};

export function scoreElectricalPanel({
    panelBrand = "unknown",
    issues = [],
    homeAge = null,
} = {}) {
    let score = 0;
    const flags = [];

    if (PANEL_BRAND_FLAGS[panelBrand]) {
        score += PANEL_BRAND_FLAGS[panelBrand].weight;
        flags.push(
            `${PANEL_BRAND_FLAGS[panelBrand].label} panels frequently require electrician evaluation and may affect insurance.`,
        );
    }

    if (issues.includes("scorch_marks")) {
        score += 6;
        flags.push("Scorch marks warrant immediate electrician review before closing.");
    }
    if (issues.includes("exposed_wiring")) score += 5;
    if (issues.includes("aluminum_wiring")) score += 4;
    if (issues.includes("outdated_fuse")) score += 4;
    if (issues.includes("insufficient_amperage")) score += 3;
    if (issues.includes("double_tapped")) score += 2;
    if (issues.includes("gfci_issues")) score += 2;
    if (issues.includes("missing_cover")) score += 2;
    if (Number(homeAge) >= 60) score += 1;

    const level = concernFromScore(score);

    let band = ELECTRICAL_RANGES.minor_repairs;
    let stance = "minor_repairs";
    if (level === "high") {
        band = ELECTRICAL_RANGES.panel_replacement;
        stance = "panel_replacement";
        if (issues.includes("aluminum_wiring")) {
            band = ELECTRICAL_RANGES.rewire_partial;
            stance = "rewire_partial";
        }
    } else if (level === "moderate") {
        band = ELECTRICAL_RANGES.panel_repair;
        stance = "panel_repair";
    }

    const followUps = [
        "Have a licensed electrician evaluate the panel in writing.",
    ];
    if (PANEL_BRAND_FLAGS[panelBrand] || level === "high") {
        followUps.push("Check insurance / lender requirements — some flag specific panels.");
    }
    if (issues.includes("aluminum_wiring")) {
        followUps.push("Aluminum wiring may require COPALUM connectors or partial rewire.");
    }

    return {
        level,
        score,
        stance,
        band: { lo: band.min, hi: band.max },
        flags,
        followUps,
    };
}

/* =========================================================================
 *  Tool 10 — Inspection Contingency Timeline Planner
 * ========================================================================= */

function daysBetween(fromISO, toISO) {
    if (!fromISO || !toISO) return null;
    const a = new Date(fromISO);
    const b = new Date(toISO);
    if (isNaN(a) || isNaN(b)) return null;
    return Math.ceil((b - a) / (1000 * 60 * 60 * 24));
}

export function planContingencyTimeline({
    inspectionDate = null,
    deadlineDate = null,
    issues = [],
    contractorQuotesNeeded = 0,
    agentInvolved = "unknown",
    underContract = "unknown",
    buyerWants = "decision_clarity",
} = {}) {
    const today = new Date().toISOString().slice(0, 10);
    const daysLeft = daysBetween(today, deadlineDate);
    const daysSinceInspection = daysBetween(inspectionDate, today);

    const urgency =
        daysLeft == null
            ? "unknown"
            : daysLeft <= 2
                ? "critical"
                : daysLeft <= 5
                    ? "high"
                    : daysLeft <= 10
                        ? "moderate"
                        : "low";

    const steps = [];
    steps.push({
        order: 1,
        title: "Review inspection report",
        detail:
            "Re-read all major-issue sections. Highlight safety, structural, and major-system concerns.",
    });
    steps.push({
        order: 2,
        title: "Identify top 3 negotiation priorities",
        detail: "Focus on impact, not item count. Cosmetic items dilute the conversation.",
    });
    if (contractorQuotesNeeded > 0) {
        steps.push({
            order: 3,
            title: `Request ${contractorQuotesNeeded} contractor quote${contractorQuotesNeeded > 1 ? "s" : ""}`,
            detail:
                "Send the relevant report excerpts only — never the full report. Ask for written quotes.",
        });
    }
    steps.push({
        order: steps.length + 1,
        title: "Align with your agent on strategy",
        detail:
            agentInvolved === "no"
                ? "Strongly consider involving an agent or attorney before submitting any written request."
                : "Decide credit vs. repair vs. price-reduction framing together.",
    });
    steps.push({
        order: steps.length + 1,
        title: "Submit written ask before the deadline",
        detail: "Document scope, doc-trail references, and any quotes. Keep timestamps clean.",
    });

    const reminders = [];
    if (urgency === "critical") {
        reminders.push("Less than 48 hours — focus on the single highest-impact item.");
    } else if (urgency === "high") {
        reminders.push("Under a week — prioritize the top 2–3 items and get quotes started today.");
    }
    if (buyerWants === "decision_clarity" && underContract === "no") {
        reminders.push(
            "Not under contract yet — use this window to decide whether to proceed, not to negotiate.",
        );
    }

    return {
        daysLeft,
        daysSinceInspection,
        urgency,
        steps,
        reminders,
    };
}

/* =========================================================================
 *  Tool 11 — Closing Cost Calculator (buyer side)
 *
 *  Rule-based estimate of buyer closing costs. Uses typical national bands
 *  per loan type, with adjustments for points buy-down, seller credits, and
 *  prepaid escrow. Cost-safe: pure JS, no external calls.
 * ========================================================================= */

// Typical buyer closing-cost ranges as a % of home price (national medians).
// Tightened for cash deals (no origination/points) and loosened for VA/FHA
// (funding/MIP fees push the upper edge up).
const CLOSING_COST_RANGES = {
    conventional: { lo: 0.020, hi: 0.040 },
    fha: { lo: 0.025, hi: 0.045 },
    va: { lo: 0.022, hi: 0.045 },
    cash: { lo: 0.005, hi: 0.015 },
    unsure: { lo: 0.020, hi: 0.050 },
};

// Rough lender-cap on seller concessions by loan type (used to clip the
// seller-credit deduction so we don't return impossible negative numbers).
const SELLER_CREDIT_CAP_PCT = {
    conventional: 0.06,
    fha: 0.06,
    va: 0.04,
    cash: 0.99,
    unsure: 0.04,
};

export function estimateClosingCosts({
    homePrice = 0,
    loanType = "conventional",
    sellerCreditPct = 0,
    pointsBuydownPct = 0,
    includePrepaids = true,
} = {}) {
    const price = Math.max(0, Number(homePrice) || 0);
    const range = CLOSING_COST_RANGES[loanType] || CLOSING_COST_RANGES.unsure;

    // Base closing cost band (lender + title + recording + transfer + escrow).
    let lo = Math.round(price * range.lo);
    let hi = Math.round(price * range.hi);

    // Prepaid escrow (taxes + insurance buffer). Roughly 0.4–0.8% on top.
    const prepaids = includePrepaids
        ? { lo: Math.round(price * 0.004), hi: Math.round(price * 0.008) }
        : { lo: 0, hi: 0 };
    lo += prepaids.lo;
    hi += prepaids.hi;

    // Points buy-down — each point ≈ 1% of LOAN amount, but for a calm
    // estimate we treat it as % of home price (close enough for planning).
    const pts = Math.max(0, Math.min(3, Number(pointsBuydownPct) || 0));
    const pointsCost = Math.round(price * (pts / 100));
    lo += pointsCost;
    hi += pointsCost;

    // Seller credit reduces the buyer's out-of-pocket. Clip by lender cap.
    const cap = SELLER_CREDIT_CAP_PCT[loanType] ?? 0.04;
    const sellerPct = Math.max(
        0, Math.min(cap, (Number(sellerCreditPct) || 0) / 100),
    );
    const sellerCredit = Math.round(price * sellerPct);
    const adjLo = Math.max(0, lo - sellerCredit);
    const adjHi = Math.max(0, hi - sellerCredit);

    // Concern level — used for the badge + funnel CTA copy. Closing costs
    // are an affordability checkpoint, so we level off home-price share.
    const sharePctHi = price > 0 ? adjHi / price : 0;
    let level = "low";
    if (sharePctHi >= 0.05) level = "high";
    else if (sharePctHi >= 0.03) level = "moderate";

    const tips = [];
    if (loanType === "fha" || loanType === "va") {
        tips.push("Your loan type carries an upfront funding/MIP fee — included in the upper band.");
    }
    if (pts > 0) {
        tips.push(`Each point lowers your rate ~0.25%; weigh that against the ~$${pointsCost.toLocaleString()} you'd pay up front.`);
    }
    if (sellerPct > 0 && sellerCredit > 0) {
        tips.push(`Seller credit applied: ~$${sellerCredit.toLocaleString()} (clipped to your loan-type cap of ${Math.round(cap * 100)}%).`);
    }
    if (loanType !== "cash") {
        tips.push("Ask your lender for a Loan Estimate — it shows itemized closing costs and is binding within tolerances.");
    }
    if (loanType === "cash") {
        tips.push("Cash deals avoid lender fees but you still pay title, recording, and transfer taxes.");
    }

    return {
        level,
        range: { lo: adjLo, hi: adjHi },
        gross: { lo, hi },
        sellerCreditApplied: sellerCredit,
        prepaidsBand: prepaids,
        pointsBuydownCost: pointsCost,
        sharePctHi: Math.round(sharePctHi * 10000) / 10000,
        tips,
    };
}
